import * as React from "react";
import { RouteComponentProps } from "react-router";

import { FriendService, SearchOptions, SearchService } from "@pro/_services";
import {
    FriendsPageActionsLarge,
    FriendsPageActionsSmall,
    FriendsPageHeaderCenter,
    FriendsSortDropdown
} from "@pro/components/Organisms/Friends";
import { InviteFriends as ModalInviteFriends } from "@pro/containers/Modals/InviteFriends";
import { PageInternal } from "@pro/containers/PageInternal";

import { SortKeys } from "@common/_constants";
import { Button } from "@common/components/Atoms/Button";
import Card, { CardItem } from "@common/components/Atoms/Card";
import Error from "@common/components/Atoms/Error";
import EmailLink from "@common/components/Atoms/Links";
import LocalTime from "@common/components/Atoms/LocalTime";
import { SearchEntry } from "@common/components/Atoms/SearchFilters";
import Sort from "@common/components/Atoms/Sort";
import FilteredList, {
    FilteredListCardsView,
    FilteredListTableView
} from "@common/components/Molecules/FilteredList";
import Modal from "@common/components/Molecules/Modal";
import { GlobalConfig } from "@common/utils";

interface FriendsProps extends RouteComponentProps<any> {
}
interface FriendsState {
    error: boolean
    errorMessage: string
    updating: boolean
    loading: boolean
    isLargeScreen: boolean
    filtersModalIsOpen: boolean
    inviteFriendsModalIsOpen: boolean
    search: SearchOptions
    searchResults: { results: any[] }
}

export class Friends extends React.Component<FriendsProps, FriendsState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);

        this.onResize = this.onResize.bind(this);
        this.onUrlChange = this.onUrlChange.bind(this);
        this.onPageChange = this.onPageChange.bind(this);
        this.onSortChange = this.onSortChange.bind(this);
        this.onKeywordSubmit = this.onKeywordSubmit.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
        this.fetchFriends = this.fetchFriends.bind(this);
        this.allFriends = this.allFriends.bind(this);

        this.emptySearchOptions = this.emptySearchOptions.bind(this);

        this.searchService = new SearchService(
            this.emptySearchOptions,
            this.fetchFriends,
            this.props.history
        );

        this.state = {
            error: false,
            errorMessage: "",
            updating: false,
            loading: false,
            isLargeScreen: false,
            filtersModalIsOpen: false,
            inviteFriendsModalIsOpen: false,
            search: this.emptySearchOptions(),
            searchResults: { results: [] }
        };
    }

    searchService: SearchService;
    removeHistoryListener: () => void;

    componentDidMount() {
        this.searchService.updateFromUrl(this.props.location);
        this.removeHistoryListener = this.props.history.listen(this.onUrlChange);

        this.onResize(); // deal with whatever size we start with....
        window.addEventListener("resize", this.onResize);
    }

    componentWillUnmount() {
        if (this.removeHistoryListener) this.removeHistoryListener();
        window.removeEventListener("resize", this.onResize);
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key => abortControllers[key].abort());
        }
    }

    onResize() {
        this.setState({
            isLargeScreen: window.matchMedia("(min-width:" + GlobalConfig.ui.breakpoints.md + "px)").matches
        });
    }

    emptySearchOptions(): SearchOptions {
        return {
            search_term: "",
            current_page: 1,
            page_size: 25,
            facets: undefined,
            order_by: SortKeys.Friends.DISPLAY_NAME
        };
    }

    allFriends() {
        this.searchService.updateUrl(this.emptySearchOptions());
    }

    fetchFriends(options) {
        this.setState({ updating: true }, () => {
            // Check to see if there already was an abortController, and if so, call abort on it
            if (this._abortControllers.search) {
                this._abortControllers.search.abort();
            }
            this._abortControllers.search = new AbortController();
            const searchSignal = this._abortControllers.search.signal;

            FriendService.search(options, searchSignal)
                .then(result => {
                    const defaultSearch = this.emptySearchOptions();
                    const search = {
                        search_term: result.search_term || defaultSearch.search_term,
                        current_page: result.current_page || defaultSearch.current_page,
                        page_size: result.page_size || defaultSearch.page_size,
                        order_by:
                            result.order_by === 0 ? 0 : result.order_by || defaultSearch.order_by
                    };

                    this.setState(
                        prevState => ({
                            ...prevState,
                            error: false,
                            loading: false,
                            updating: false,
                            search,
                            searchResults: result
                        }),
                        () => {
                            window.scrollTo(0, 0);
                        }
                    );
                })
                .catch(err => {
                    console.log(err);
                    if (err.type === "abort" || err.name === "AbortError") {
                        this.setState({ updating: false, loading: false });
                        return;
                    }

                    this.setState({
                        error: true,
                        errorMessage: err.message,
                        updating: false,
                        loading: false
                    });
                });
        });
    }

    closeModal(modal) {
        const newState = {};
        newState[modal] = false;
        this.setState(newState);
    }

    openModal(modal) {
        const newState = {};
        newState[modal] = true;
        this.setState(newState);
    }

    onUrlChange(location) {
        this.searchService.updateFromUrl(location);
    }

    onPageChange(pageNumber) {
        this.searchService.updateUrl({
            ...this.state.search,
            current_page: pageNumber
        });
    }

    onSortChange(sort) {
        this.searchService.updateUrl({
            ...this.state.search,
            order_by: sort,
            current_page: 1
        });
    }

    onKeywordSubmit(keyword) {
        this.closeModal("filtersModalIsOpen");
        // reset all search/paging stuff when doing a keyword search
        this.searchService.updateUrl({
            ...this.emptySearchOptions(),
            search_term: keyword
        });
    }

    onFiltersButtonClick() {
        this.closeModal("filtersModalIsOpen");
    }

    render() {
        const { error, isLargeScreen } = this.state;

        if (error) {
            return (
                <PageInternal title="friends">
                    <Error show={this.state.error} description={this.state.errorMessage} />
                </PageInternal>
            );
        } else if (this.state.searchResults.results.length === 0) {
            return (
                <PageInternal title="friends" updating={this.state.updating}>
                    <h2 className="c-heading c-heading--purple">
                        refer friends to become relode agents
                    </h2>
                    <div className="u-text-center">
                        <Button
                            id="invite-friends-button"
                            large={true}
                            green={true}
                            onClick={() => {
                                this.openModal("inviteFriendsModalIsOpen");
                            }}>
                            invite friends
                        </Button>
                    </div>

                    <ModalInviteFriends
                        isOpen={this.state.inviteFriendsModalIsOpen}
                        closeModal={() => this.closeModal("inviteFriendsModalIsOpen")}
                    />
                </PageInternal>
            );
        }

        return (
            <PageInternal
                title="friends"
                center={
                    <FriendsPageHeaderCenter
                        value={this.state.search.search_term || ""}
                        onKeywordSubmit={this.onKeywordSubmit}
                    />
                }
                actionsLarge={
                    <FriendsPageActionsLarge
                        openInviteFriendsModal={() => this.openModal("inviteFriendsModalIsOpen")}
                    />
                }
                actionsSmall={
                    <FriendsPageActionsSmall
                        openInviteFriendsModal={() => this.openModal("inviteFriendsModalIsOpen")}
                    />
                }
                updating={this.state.updating}>
                <FilteredList>
                    <FilteredListTableView
                        locationSearchTerm={this.state.search.search_term}
                        responsive={true}
                        data={this.state.searchResults}
                        onPageChange={this.onPageChange}
                        onFacetChange={() => { return; }}
                        onDistanceChange={() => { return; }}
                        isLargeScreen={isLargeScreen}>
                        <table className="c-table c-table--friends">
                            <thead>
                                <tr>
                                    <th>
                                        <Sort
                                            sortKey={SortKeys.Friends.DISPLAY_NAME}
                                            orderBy={this.state.search.order_by || 0}
                                            onSortChange={() =>
                                                this.onSortChange(SortKeys.Friends.DISPLAY_NAME)
                                            }>
                                            Name
                                        </Sort>
                                    </th>
                                    <th>location</th>
                                    <th>
                                        <Sort
                                            sortKey={SortKeys.Friends.CREATE_TIMESTAMP}
                                            orderBy={this.state.search.order_by || 0}
                                            onSortChange={() =>
                                                this.onSortChange(SortKeys.Friends.CREATE_TIMESTAMP)
                                            }>
                                            Joined
                                        </Sort>
                                    </th>
                                    <th>review</th>
                                    <th>consideration</th>
                                    <th>eliminated</th>
                                    <th>hired</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.searchResults.results.map(friend => (
                                    <tr key={friend.user_id}>
                                        <td>
                                            <div className="c-table__friend-name">
                                                {friend.name}
                                            </div>
                                            <EmailLink email={friend.email} />
                                        </td>
                                        <td>{friend.location}</td>
                                        <td>
                                            <LocalTime utcTimestamp={friend.joined_timestamp} />
                                        </td>
                                        <td>{friend.in_review}</td>
                                        <td>{friend.in_consideration}</td>
                                        <td>{friend.in_eliminated}</td>
                                        <td>{friend.in_hired}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </FilteredListTableView>

                    <FilteredListCardsView
                        responsive={true}
                        data={this.state.searchResults}
                        onPageChange={this.onPageChange}
                        onFiltersButtonClick={() => this.openModal("filtersModalIsOpen")}
                        sortDropdown={<FriendsSortDropdown onSortChange={this.onSortChange} />}
                        isLargeScreen={isLargeScreen}>
                        {this.state.searchResults.results.map(friend => (
                            <Card id={friend.user_id} title={`${friend.name}`} key={friend.user_id}>
                                <CardItem bullet={true}>
                                    <EmailLink email={friend.email} />
                                </CardItem>
                                {friend.location && <CardItem bullet={true}>{friend.location}</CardItem>}
                                <CardItem bullet={true}>
                                    joined <LocalTime utcTimestamp={friend.joined_timestamp} />
                                </CardItem>
                                <CardItem dotted={true}>
                                    <span>review</span>
                                    <span>{friend.in_review}</span>
                                </CardItem>
                                <CardItem dotted={true}>
                                    <span>consideration</span>
                                    <span>{friend.in_consideration}</span>
                                </CardItem>
                                <CardItem dotted={true}>
                                    <span>eliminated</span>
                                    <span>{friend.in_eliminated}</span>
                                </CardItem>
                                <CardItem dotted={true}>
                                    <span>hired</span>
                                    <span>{friend.in_hired}</span>
                                </CardItem>
                            </Card>
                        ))}
                    </FilteredListCardsView>
                </FilteredList>

                <Modal
                    className="c-modal--filters"
                    size="full"
                    isOpen={this.state.filtersModalIsOpen}
                    closeModal={() => this.closeModal("filtersModalIsOpen")}>
                    <div className="c-modal__filters">
                        <div className="c-modal__filters-title">filters</div>
                        <SearchEntry
                            label="keywords"
                            value={this.state.search.search_term}
                            overlay={true}
                            dark={true}
                            onSubmit={this.onKeywordSubmit}
                            submitOnEnter={true}
                        />
                    </div>
                </Modal>

                <ModalInviteFriends
                    isOpen={this.state.inviteFriendsModalIsOpen}
                    closeModal={() => this.closeModal("inviteFriendsModalIsOpen")}
                />
            </PageInternal>
        );
    }
}
